import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import { defaultPreferencesDocument, mainActions } from "../store";
import { exportSyncedPreferencesDocument } from "../store/personalData";
import { useAuth } from "./AuthProvider";
import { getPreferences, preferencesErrorMessage, savePreferences } from "./preferencesRepository";

const SAVE_DELAY = 500;

export const AccountPreferencesContext = createContext({
  status: "idle",
  error: "",
});
export const useAccountPreferences = () => useContext(AccountPreferencesContext);

function currentDocument(store) {
  return exportSyncedPreferencesDocument(store.getState(), defaultPreferencesDocument.chartDefaults);
}

function mergePreferenceGroups(initial, latest, saved) {
  const merged = { schemaVersion: 1 };
  for (const group of ["chartDefaults", "earTrainer", "fretboard", "metronome"]) {
    const localChanged = JSON.stringify(latest[group]) !== JSON.stringify(initial[group]);
    merged[group] = localChanged || !saved ? latest[group] : saved.document[group];
  }
  return merged;
}

const AccountPreferencesProvider = ({ children }) => {
  const auth = useAuth();
  const store = useStore();
  const userId = auth.session?.user.id;
  const accessToken = auth.session?.access_token;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ userId, status: "idle", error: "" });
  const authRef = useRef(auth);
  authRef.current = auth;

  useEffect(() => {
    let active = true;
    let timer = null;
    let unsubscribe = null;
    const controller = new AbortController();
    const currentAuth = authRef.current;
    const owner = currentAuth.session?.user.id;
    const token = currentAuth.session?.access_token;

    if (!currentAuth.configured || currentAuth.loading || currentAuth.error || !owner || !token || !currentAuth.client) {
      setState({ userId: owner, status: "idle", error: "" });
      return () => { active = false; controller.abort(); };
    }

    const context = { client: currentAuth.client, accessToken: token, signal: controller.signal };
    setState({ userId: owner, status: "loading", error: "" });

    const initialize = async () => {
      let initialDocument;
      try {
        initialDocument = currentDocument(store);
        const saved = await getPreferences(context);
        if (!active) return;

        let revision = saved?.revision ?? null;
        const latestDocument = currentDocument(store);
        const reconciledDocument = mergePreferenceGroups(initialDocument, latestDocument, saved);
        store.dispatch(mainActions.loadPreferencesDocument(reconciledDocument));
        let baseline = JSON.stringify(reconciledDocument);

        if (!saved || baseline !== JSON.stringify(saved.document)) {
          const result = await savePreferences(context, revision, reconciledDocument);
          if (!active) return;
          revision = result.revision;
          baseline = JSON.stringify(result.document);
        }

        let saving = false;
        let paused = false;
        const updateStatus = (status, error = "") => {
          if (active) setState({ userId: owner, status, error });
        };
        const scheduleSave = () => {
          if (!active || paused) return;
          if (timer !== null) clearTimeout(timer);
          timer = setTimeout(() => {
            timer = null;
            void persistLatest();
          }, SAVE_DELAY);
        };
        const persistLatest = async () => {
          if (!active || paused || saving) return;
          let document;
          let fingerprint;
          try {
            document = currentDocument(store);
            fingerprint = JSON.stringify(document);
          } catch (error) {
            paused = true;
            updateStatus("error", preferencesErrorMessage(error));
            return;
          }
          if (fingerprint === baseline) {
            updateStatus("synced");
            return;
          }

          saving = true;
          updateStatus("syncing");
          try {
            const result = await savePreferences(context, revision, document);
            if (!active) return;
            revision = result.revision;
            baseline = JSON.stringify(result.document);
            updateStatus("synced");
          } catch (error) {
            if (!active) return;
            paused = true;
            updateStatus("error", preferencesErrorMessage(error));
          } finally {
            saving = false;
            if (active && !paused) {
              try {
                if (JSON.stringify(currentDocument(store)) !== baseline) scheduleSave();
              } catch (error) {
                paused = true;
                updateStatus("error", preferencesErrorMessage(error));
              }
            }
          }
        };

        updateStatus("synced");
        unsubscribe = store.subscribe(() => {
          if (paused) return;
          try {
            if (JSON.stringify(currentDocument(store)) !== baseline) scheduleSave();
          } catch (error) {
            paused = true;
            updateStatus("error", preferencesErrorMessage(error));
          }
        });
        if (JSON.stringify(currentDocument(store)) !== baseline) scheduleSave();
      } catch (error) {
        if (active) setState({ userId: owner, status: "error", error: preferencesErrorMessage(error) });
      }
    };

    void initialize();
    return () => {
      active = false;
      controller.abort();
      if (timer !== null) clearTimeout(timer);
      unsubscribe?.();
    };
  }, [accessToken, attempt, auth.configured, auth.error, auth.loading, store, userId]);

  const visibleState = state.userId === userId ? state : { status: "idle", error: "" };
  return <AccountPreferencesContext.Provider value={{
    ...visibleState,
    retry: () => setAttempt(value => value + 1),
  }}>{children}</AccountPreferencesContext.Provider>;
};

export default AccountPreferencesProvider;
